import { format } from 'date-fns'
import { useState } from 'react'
import { Link } from 'react-router'

import { SkeletonRows } from '@/components/ui/skeleton'
import { useStaffUserReminders } from '@/models/staffUser'
import { type Reminder, reminderAction } from '@/models/userReminder'

interface Section {
  title: string
  items: Reminder[]
}

function sections(reminders: Reminder[], now: number): Section[] {
  const open = reminders.filter((r) => !r.completedAt)
  return [
    { title: 'Upcoming', items: open.filter((r) => new Date(r.reminderDate).getTime() >= now) },
    { title: 'Overdue', items: open.filter((r) => new Date(r.reminderDate).getTime() < now) },
    { title: 'Completed', items: reminders.filter((r) => r.completedAt) },
  ].filter((section) => section.items.length > 0)
}

/** Read-only: completing a reminder is for its owner, on My Reminders. */
export function StaffRemindersTab({ staffUserId }: { staffUserId: string }) {
  const { reminders, isPending, isError } = useStaffUserReminders(staffUserId)
  const [now] = useState(() => Date.now())

  if (isPending) return <SkeletonRows rows={3} />
  if (isError) return <p className="text-destructive py-6 text-center text-sm">Couldn’t load reminders.</p>
  if (reminders.length === 0)
    return <p className="text-muted-foreground py-6 text-center text-sm">No reminders.</p>

  return (
    <div className="flex flex-col gap-6">
      {sections(reminders, now).map((section) => (
        <section key={section.title} aria-label={section.title}>
          <h2 className="mb-2 text-sm font-semibold text-neutral-500">{section.title}</h2>
          <ul className="flex flex-col gap-2">
            {section.items.map((reminder) => {
              const cta = reminderAction(reminder)
              return (
                <li
                  key={reminder.id}
                  className="bg-surface flex items-center justify-between gap-3 rounded-md border border-neutral-200 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-md truncate">{reminder.message}</p>
                    {cta && (
                      <Link to={cta.to} className="text-sm text-blue-600 underline-offset-2 hover:underline">
                        {reminder.lead ? reminder.lead.name : cta.label}
                      </Link>
                    )}
                  </div>
                  <span className="shrink-0 text-sm text-neutral-500">
                    {format(new Date(reminder.completedAt ?? reminder.reminderDate), 'MMM d, h:mm a')}
                  </span>
                </li>
              )
            })}
          </ul>
        </section>
      ))}
    </div>
  )
}
